/**
 * Human-input rules for Bottle clicker.
 *
 * Two separate jobs, both pure so the playfield and the tests can share them:
 *  - a token bucket that lets a human burst but caps the sustained tap rate,
 *  - a pattern check that spots a macro (metronomic gaps, cursor nailed down).
 *
 * A flagged tap is not thrown away: it still returns the bottle, it just pays
 * like a zbieracz picked it up, with no click bonus and no combo. That keeps a
 * false positive from stealing kaucja outright.
 */

export const BOTTLE_CLICKER_INPUT = {
  /** Taps available for a burst before the bucket has to refill. */
  BURST_TAPS: 20,
  /** Sustained taps per second a human can keep up; the bucket refills at this rate. */
  SUSTAINED_TAPS_PER_SEC: 10,
  /** Gaps below this are fast enough to be worth checking for a macro. */
  MACRO_MAX_GAP_MS: 240,
  /** Taps inspected by the pattern check. */
  SAMPLE_SIZE: 20,
  /**
   * Motor noise puts a rapid human tapper somewhere north of 10 ms of spread.
   * Only a timer holds nineteen gaps inside a few milliseconds of each other.
   */
  MIN_GAP_JITTER_MS: 4,
  /** A cursor this pinned across twenty taps is not being held by a hand. */
  MIN_POINTER_DRIFT_PX: 4,
  /** How long a flagged pattern keeps paying collector rates. */
  FLAG_MS: 10_000,
} as const;

export type BottleClickerTapVerdict = "human" | "throttled" | "macro";

export interface BottleClickerTap {
  atMs: number;
  x: number;
  y: number;
}

export interface BottleClickerInputState {
  /** Whole taps left in the bucket, fractional refill included. */
  taps: number;
  lastTapMs: number;
  recent: readonly BottleClickerTap[];
  flaggedUntilMs: number;
}

export function emptyBottleClickerInputState(nowMs = 0): BottleClickerInputState {
  return {
    taps: BOTTLE_CLICKER_INPUT.BURST_TAPS,
    lastTapMs: nowMs,
    recent: [],
    flaggedUntilMs: 0,
  };
}

function standardDeviation(values: readonly number[]): number {
  if (values.length < 2) return Number.POSITIVE_INFINITY;
  const mean = values.reduce((sum, value) => sum + value, 0) / values.length;
  const variance =
    values.reduce((sum, value) => sum + (value - mean) ** 2, 0) / (values.length - 1);
  return Math.sqrt(variance);
}

/** True when the recent taps are too even and too still to be a hand. */
export function looksLikeMacro(taps: readonly BottleClickerTap[]): boolean {
  if (taps.length < BOTTLE_CLICKER_INPUT.SAMPLE_SIZE) return false;

  const gaps: number[] = [];
  for (let i = 1; i < taps.length; i += 1) {
    const previous = taps[i - 1];
    const current = taps[i];
    if (!previous || !current) return false;
    gaps.push(current.atMs - previous.atMs);
  }
  if (gaps.some((gap) => gap > BOTTLE_CLICKER_INPUT.MACRO_MAX_GAP_MS)) return false;
  if (standardDeviation(gaps) >= BOTTLE_CLICKER_INPUT.MIN_GAP_JITTER_MS) return false;

  const xs = taps.map((tap) => tap.x);
  const ys = taps.map((tap) => tap.y);
  const drift = Math.hypot(
    Math.max(...xs) - Math.min(...xs),
    Math.max(...ys) - Math.min(...ys),
  );
  return drift < BOTTLE_CLICKER_INPUT.MIN_POINTER_DRIFT_PX;
}

/**
 * Spend a tap from the bucket and judge the pattern it belongs to.
 * "throttled" taps are dropped by the caller; "macro" taps pay collector rates.
 *
 * Every tap joins the sample, including throttled ones. Sampling only the taps
 * that survive the bucket would record one tap per refill period exactly, which
 * makes a fast human look like a metronome.
 */
export function judgeTap(
  state: BottleClickerInputState,
  tap: BottleClickerTap,
): { state: BottleClickerInputState; verdict: BottleClickerTapVerdict } {
  const recent = [...state.recent, tap].slice(-BOTTLE_CLICKER_INPUT.SAMPLE_SIZE);
  const flaggedUntilMs = looksLikeMacro(recent)
    ? tap.atMs + BOTTLE_CLICKER_INPUT.FLAG_MS
    : state.flaggedUntilMs;

  const elapsedSec = Math.max(0, tap.atMs - state.lastTapMs) / 1000;
  const refilled = Math.min(
    BOTTLE_CLICKER_INPUT.BURST_TAPS,
    state.taps + elapsedSec * BOTTLE_CLICKER_INPUT.SUSTAINED_TAPS_PER_SEC,
  );

  if (refilled < 1) {
    return {
      state: { taps: refilled, lastTapMs: tap.atMs, recent, flaggedUntilMs },
      verdict: "throttled",
    };
  }

  return {
    state: { taps: refilled - 1, lastTapMs: tap.atMs, recent, flaggedUntilMs },
    verdict: tap.atMs < flaggedUntilMs ? "macro" : "human",
  };
}

export function isInputFlagged(state: BottleClickerInputState, nowMs: number): boolean {
  return nowMs < state.flaggedUntilMs;
}
